import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import tradingSalesInvoiceService from "../services/tradingSalesInvoiceService";

const getResponseData = (response) =>
  response?.data?.data ?? response?.data ?? {};

const getErrorMessage = (err) =>
  err?.response?.data?.message ||
  err?.response?.data?.error ||
  err?.message ||
  "Unable to process Sales Invoice.";

const formatNumber = (value) =>
  Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatDate = (value) => {
  if (!value) return "";
  return String(value).slice(0, 10);
};

const styles = {
  page: {
    padding: "24px 28px",
    background: "#f6f7fb",
    minHeight: "100vh",
    color: "#243047",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 12,
    marginBottom: 22,
  },
  title: {
    margin: 0,
    fontSize: 23,
    fontWeight: 750,
    color: "#172554",
  },
  subtitle: {
    color: "#64748b",
    fontSize: 12,
    marginTop: 5,
  },
  card: {
    background: "#fff",
    border: "1px solid #e4e8f1",
    borderRadius: 11,
    padding: 20,
    marginBottom: 18,
    boxShadow: "0 2px 8px rgba(20,35,70,0.035)",
  },
  sectionTitle: {
    fontSize: 15,
    fontWeight: 750,
    color: "#172554",
    margin: "0 0 5px",
  },
  muted: {
    fontSize: 12,
    color: "#718096",
  },
  label: {
    display: "block",
    fontSize: 12,
    fontWeight: 650,
    color: "#475569",
    marginBottom: 6,
  },
  input: {
    width: "100%",
    padding: "9px 11px",
    border: "1px solid #d8deea",
    borderRadius: 7,
    fontSize: 13,
    boxSizing: "border-box",
    background: "#fff",
  },
  readonly: {
    width: "100%",
    padding: "10px 11px",
    border: "1px solid #e5e9f2",
    borderRadius: 7,
    fontSize: 13,
    boxSizing: "border-box",
    background: "#f8fafc",
    color: "#475569",
  },
  primaryButton: {
    background: "linear-gradient(120deg,#6254df,#4936c9)",
    border: 0,
    borderRadius: 8,
    color: "#fff",
    padding: "10px 18px",
    fontWeight: 700,
    cursor: "pointer",
  },
  secondaryButton: {
    background: "#fff",
    border: "1px solid #d8deea",
    borderRadius: 8,
    color: "#334155",
    padding: "9px 15px",
    fontWeight: 650,
    cursor: "pointer",
  },
  tableWrap: {
    width: "100%",
    overflowX: "auto",
    border: "1px solid #e5e9f2",
    borderRadius: 8,
  },
  table: {
    width: "100%",
    minWidth: 850,
    borderCollapse: "collapse",
    fontSize: 12,
  },
  th: {
    background: "#f3f5fa",
    color: "#475569",
    textAlign: "left",
    padding: "12px 10px",
    fontSize: 10,
    fontWeight: 750,
    whiteSpace: "nowrap",
    borderBottom: "1px solid #e2e8f0",
  },
  td: {
    padding: "11px 10px",
    borderBottom: "1px solid #edf0f5",
    verticalAlign: "middle",
  },
  error: {
    background: "#fff1f2",
    color: "#be123c",
    border: "1px solid #fecdd3",
    padding: 12,
    borderRadius: 8,
    marginBottom: 15,
    fontSize: 13,
  },
  info: {
    background: "#eff6ff",
    color: "#1d4ed8",
    border: "1px solid #bfdbfe",
    padding: 12,
    borderRadius: 8,
    marginBottom: 15,
    fontSize: 12,
  },
};

function SummaryCard({ label, value, color }) {
  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e5e9f2",
        borderLeft: `4px solid ${color}`,
        borderRadius: 9,
        padding: "14px 16px",
      }}
    >
      <div style={{ fontSize: 11, color: "#64748b", fontWeight: 650 }}>
        {label}
      </div>
      <div style={{ fontSize: 21, fontWeight: 750, marginTop: 7 }}>
        {value}
      </div>
    </div>
  );
}

export default function TradingSalesInvoiceEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [invoice, setInvoice] = useState(null);
  const [items, setItems] = useState([]);
  const [charges, setCharges] = useState([]);

  const [form, setForm] = useState({
    invoice_date: "",
    due_date: "",
    customer_reference: "",
    payment_terms: "",
    billing_address: "",
    shipping_address: "",
    remarks: "",
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const loadInvoice = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const response =
        await tradingSalesInvoiceService.getSalesInvoiceDetail(id);

      const body = getResponseData(response);
      const detail = body.invoice || body.data || body;

      if (!detail?.id) {
        throw new Error("Sales Invoice details were not returned.");
      }

      if (String(detail.status_code || "").toUpperCase() !== "DRAFT") {
        throw new Error("Only Draft Sales Invoices can be edited.");
      }

      setInvoice(detail);

      setForm({
        invoice_date: formatDate(detail.invoice_date),
        due_date: formatDate(detail.due_date),
        customer_reference: detail.customer_reference || "",
        payment_terms: detail.payment_terms || "",
        billing_address: detail.billing_address || "",
        shipping_address: detail.shipping_address || "",
        remarks: detail.remarks || "",
      });

      setItems(
        (Array.isArray(detail.items) ? detail.items : []).map((item) => ({
          ...item,
          invoice_quantity: String(item.invoice_quantity ?? ""),
        }))
      );

      setCharges(
        (Array.isArray(detail.charges) ? detail.charges : []).map(
          (charge) => ({
            ...charge,
            charge_type: charge.charge_type || "",
            description: charge.description || "",
            charge_amount: String(charge.charge_amount ?? 0),
            gst_rate: String(charge.gst_rate ?? 0),
            remarks: charge.remarks || "",
          })
        )
      );
    } catch (err) {
      setInvoice(null);
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadInvoice();
  }, [loadInvoice]);

  const updateForm = (field, value) => {
    setForm((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  const updateQuantity = (itemId, value) => {
    setItems((previous) =>
      previous.map((item) =>
        Number(item.id) === Number(itemId)
          ? { ...item, invoice_quantity: value }
          : item
      )
    );
  };

  const addCharge = () => {
    setCharges((previous) => [
      ...previous,
      {
        charge_type: "",
        description: "",
        charge_amount: "0",
        gst_rate: "0",
        remarks: "",
      },
    ]);
  };

  const updateCharge = (index, field, value) => {
    setCharges((previous) =>
      previous.map((charge, i) =>
        i === index ? { ...charge, [field]: value } : charge
      )
    );
  };

  const removeCharge = (index) => {
    setCharges((previous) =>
      previous.filter((_, i) => i !== index)
    );
  };

  const quantityTotal = useMemo(
    () =>
      items.reduce(
        (total, item) => total + Number(item.invoice_quantity || 0),
        0
      ),
    [items]
  );

  const chargeTotal = useMemo(
    () =>
      charges.reduce(
        (total, charge) =>
          total + Number(charge.charge_amount || 0),
        0
      ),
    [charges]
  );

  const validate = () => {
    if (!items.length) {
      return "Invoice must contain at least one item.";
    }

    for (const item of items) {
      const quantity = Number(item.invoice_quantity);

      if (!Number.isFinite(quantity) || quantity <= 0) {
        return `Enter a valid invoice quantity for ${item.item_description || "the selected item"}.`;
      }
    }

    for (const charge of charges) {
      if (!String(charge.charge_type || "").trim()) {
        return "Please enter the charge type.";
      }

      if (
        !Number.isFinite(Number(charge.charge_amount)) ||
        Number(charge.charge_amount) < 0
      ) {
        return "Enter a valid charge amount.";
      }

      if (
        !Number.isFinite(Number(charge.gst_rate)) ||
        Number(charge.gst_rate) < 0
      ) {
        return "Enter a valid GST rate.";
      }
    }

    return "";
  };

  const saveChanges = async () => {
    const validationError = validate();

    if (validationError) {
      setError(validationError);
      return;
    }

    setSaving(true);
    setError("");

    try {
      const payload = {
        ...form,

        items: items.map((item) => ({
          delivery_challan_id: Number(item.delivery_challan_id),
          delivery_challan_item_id: Number(
            item.delivery_challan_item_id
          ),
          invoice_quantity: Number(item.invoice_quantity),
        })),

        charges: charges.map((charge) => ({
          charge_type: charge.charge_type.trim(),
          description: charge.description.trim() || null,
          charge_amount: Number(charge.charge_amount || 0),
          gst_rate: Number(charge.gst_rate || 0),
          remarks: charge.remarks.trim() || null,
        })),
      };

      await tradingSalesInvoiceService.updateSalesInvoice(id, payload);

      navigate(`/trading/sales-invoices/${id}`);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>Loading Sales Invoice...</div>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div style={styles.page}>
        <div style={styles.header}>
          <div>
            <h1 style={styles.title}>Edit Sales Invoice</h1>
            <p style={styles.subtitle}>Invoice editing workspace</p>
          </div>

          <button
            style={styles.secondaryButton}
            onClick={() => navigate("/trading/sales-invoices")}
          >
            ← Back to Invoice Register
          </button>
        </div>

        <div role="alert" style={styles.error}>
          {error || "Sales Invoice not found."}
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Edit Sales Invoice</h1>
          <div style={styles.subtitle}>
            Modify Draft Invoice — {invoice.invoice_no}
          </div>
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button
            type="button"
            style={styles.secondaryButton}
            onClick={() => navigate(`/trading/sales-invoices/${id}`)}
            disabled={saving}
          >
            Cancel
          </button>

          <button
            type="button"
            style={{
              ...styles.primaryButton,
              opacity: saving ? 0.6 : 1,
            }}
            onClick={saveChanges}
            disabled={saving}
          >
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>

      {error && (
        <div role="alert" style={styles.error}>
          {error}
        </div>
      )}

      <div style={styles.info}>
        Customer, Sales Order and source Delivery Challans are fixed for this
        invoice. Item prices, discounts and GST are recalculated by the
        backend from the original Sales Order.
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3,minmax(0,1fr))",
          gap: 14,
          marginBottom: 18,
        }}
      >
        <SummaryCard
          label="Invoice Lines"
          value={items.length}
          color="#6254df"
        />
        <SummaryCard
          label="Invoice Quantity"
          value={formatNumber(quantityTotal)}
          color="#0d91df"
        />
        <SummaryCard
          label="Additional Charges"
          value={`₹ ${formatNumber(chargeTotal)}`}
          color="#16a36a"
        />
      </div>

      {/* CUSTOMER AND SALES ORDER */}
      <div style={styles.card}>
        <h3 style={styles.sectionTitle}>Customer & Sales Order Details</h3>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(2,minmax(0,1fr))",
            gap: 16,
            marginTop: 16,
          }}
        >
          <div>
            <label style={styles.label}>Customer</label>
            <div style={styles.readonly}>
              {invoice.customer_name || invoice.customer?.bp_name || "-"}
            </div>
          </div>

          <div>
            <label style={styles.label}>Sales Order</label>
            <div style={styles.readonly}>
              {invoice.sales_order?.sales_order_no ||
                invoice.sales_order_no ||
                invoice.sales_order_id ||
                "-"}
            </div>
          </div>

          <div>
            <label style={styles.label}>Invoice Number</label>
            <div style={styles.readonly}>{invoice.invoice_no}</div>
          </div>

          <div>
            <label style={styles.label}>Status</label>
            <div style={styles.readonly}>DRAFT</div>
          </div>
        </div>
      </div>

      {/* INVOICE HEADER */}
      <div style={styles.card}>
        <h3 style={styles.sectionTitle}>Invoice Details</h3>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(2,minmax(0,1fr))",
            gap: 16,
            marginTop: 16,
          }}
        >
          <div>
            <label style={styles.label}>Invoice Date *</label>
            <input
              type="date"
              style={styles.input}
              value={form.invoice_date}
              onChange={(e) => updateForm("invoice_date", e.target.value)}
            />
          </div>

          <div>
            <label style={styles.label}>Due Date</label>
            <input
              type="date"
              style={styles.input}
              value={form.due_date}
              onChange={(e) => updateForm("due_date", e.target.value)}
            />
          </div>

          <div>
            <label style={styles.label}>Customer Reference</label>
            <input
              style={styles.input}
              value={form.customer_reference}
              onChange={(e) =>
                updateForm("customer_reference", e.target.value)
              }
              placeholder="Customer reference"
            />
          </div>

          <div>
            <label style={styles.label}>Payment Terms</label>
            <input
              style={styles.input}
              value={form.payment_terms}
              onChange={(e) =>
                updateForm("payment_terms", e.target.value)
              }
              placeholder="Payment terms"
            />
          </div>

          <div>
            <label style={styles.label}>Billing Address</label>
            <textarea
              style={{ ...styles.input, minHeight: 75 }}
              value={form.billing_address}
              onChange={(e) =>
                updateForm("billing_address", e.target.value)
              }
            />
          </div>

          <div>
            <label style={styles.label}>Shipping Address</label>
            <textarea
              style={{ ...styles.input, minHeight: 75 }}
              value={form.shipping_address}
              onChange={(e) =>
                updateForm("shipping_address", e.target.value)
              }
            />
          </div>

          <div style={{ gridColumn: "1 / -1" }}>
            <label style={styles.label}>Remarks</label>
            <textarea
              style={{ ...styles.input, minHeight: 75 }}
              value={form.remarks}
              onChange={(e) => updateForm("remarks", e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* SOURCE DELIVERY CHALLANS */}
      <div style={styles.card}>
        <h3 style={styles.sectionTitle}>Source Delivery Challans</h3>
        <p style={styles.muted}>
          The original Delivery Challan references cannot be changed during
          invoice editing.
        </p>

        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>DELIVERY CHALLAN</th>
                <th style={styles.th}>DATE</th>
                <th style={styles.th}>ITEM</th>
              </tr>
            </thead>

            <tbody>
              {items.map((item, index) => (
                <tr key={item.id || index}>
                  <td style={styles.td}>
                    {item.delivery_challan_no ||
                      item.delivery_challan_id ||
                      "-"}
                  </td>
                  <td style={styles.td}>
                    {formatDate(item.delivery_challan_date) || "-"}
                  </td>
                  <td style={styles.td}>
                    {item.item_description || "-"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* INVOICE ITEMS */}
      <div style={styles.card}>
        <h3 style={styles.sectionTitle}>Invoice Items</h3>
        <p style={styles.muted}>
          Update invoice quantities. The backend validates the remaining
          quantity against the source Delivery Challan.
        </p>

        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>ITEM</th>
                <th style={styles.th}>SOURCE DC</th>
                <th style={styles.th}>RATE</th>
                <th style={styles.th}>DISCOUNT %</th>
                <th style={styles.th}>INVOICE QTY</th>
              </tr>
            </thead>

            <tbody>
              {items.map((item, index) => (
                <tr key={item.id || index}>
                  <td style={styles.td}>
                    <strong>{item.item_description || "-"}</strong>
                    <div style={styles.muted}>
                      Item ID: {item.raw_material_id || "-"}
                    </div>
                  </td>

                  <td style={styles.td}>
                    {item.delivery_challan_no ||
                      item.delivery_challan_id ||
                      "-"}
                  </td>

                  <td style={styles.td}>
                    {formatNumber(item.unit_selling_price)}
                  </td>

                  <td style={styles.td}>
                    {formatNumber(item.discount_percent)}
                  </td>

                  <td style={styles.td}>
                    <input
                      type="number"
                      min="0"
                      step="0.001"
                      style={{ ...styles.input, width: 130 }}
                      value={item.invoice_quantity}
                      onChange={(e) =>
                        updateQuantity(item.id, e.target.value)
                      }
                    />
                  </td>
                </tr>
              ))}

              {!items.length && (
                <tr>
                  <td colSpan={5} style={styles.td}>
                    No invoice items found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ADDITIONAL CHARGES */}
      <div style={styles.card}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 10,
          }}
        >
          <div>
            <h3 style={styles.sectionTitle}>Additional Charges</h3>
            <p style={styles.muted}>
              Add, edit or remove customer-billable charges.
            </p>
          </div>

          <button
            type="button"
            style={styles.secondaryButton}
            onClick={addCharge}
          >
            + Add Charge
          </button>
        </div>

        {charges.map((charge, index) => (
          <div
            key={index}
            style={{
              display: "grid",
              gridTemplateColumns: "1.1fr 1.5fr 1fr 0.8fr auto",
              gap: 10,
              alignItems: "end",
              padding: "15px 0",
              borderBottom: "1px solid #edf0f5",
            }}
          >
            <div>
              <label style={styles.label}>Charge Type</label>
              <input
                style={styles.input}
                value={charge.charge_type}
                onChange={(e) =>
                  updateCharge(index, "charge_type", e.target.value)
                }
              />
            </div>

            <div>
              <label style={styles.label}>Description</label>
              <input
                style={styles.input}
                value={charge.description}
                onChange={(e) =>
                  updateCharge(index, "description", e.target.value)
                }
              />
            </div>

            <div>
              <label style={styles.label}>Amount</label>
              <input
                type="number"
                min="0"
                style={styles.input}
                value={charge.charge_amount}
                onChange={(e) =>
                  updateCharge(index, "charge_amount", e.target.value)
                }
              />
            </div>

            <div>
              <label style={styles.label}>GST %</label>
              <input
                type="number"
                min="0"
                style={styles.input}
                value={charge.gst_rate}
                onChange={(e) =>
                  updateCharge(index, "gst_rate", e.target.value)
                }
              />
            </div>

            <button
              type="button"
              onClick={() => removeCharge(index)}
              style={{
                ...styles.secondaryButton,
                color: "#be123c",
              }}
            >
              Remove
            </button>

            <div style={{ gridColumn: "1 / -1" }}>
              <label style={styles.label}>Remarks</label>
              <input
                style={styles.input}
                value={charge.remarks}
                onChange={(e) =>
                  updateCharge(index, "remarks", e.target.value)
                }
              />
            </div>
          </div>
        ))}

        {!charges.length && (
          <p style={styles.muted}>No additional charges added.</p>
        )}
      </div>

      {/* FOOTER ACTIONS */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
          marginBottom: 30,
        }}
      >
        <button
          type="button"
          style={styles.secondaryButton}
          onClick={() => navigate(`/trading/sales-invoices/${id}`)}
          disabled={saving}
        >
          ← Back to Invoice
        </button>

        <button
          type="button"
          style={styles.primaryButton}
          onClick={saveChanges}
          disabled={saving}
        >
          {saving ? "Saving..." : "Save Changes"}
        </button>
      </div>
    </div>
  );
}